import { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, AlertCircle, RotateCcw, Save, Loader2,
  LayoutGrid, Settings2, Mail, History, StickyNote, ListTodo, CalendarDays,
  ChevronDown, Flag,
} from "lucide-react";

import { API } from "@/lib/api";
import { readToken } from "@/lib/tokenStore";
import {
  PRIORITY_OPTIONS,
} from "@/pages/journey/leadOptions";
import NotesTab from "@/pages/journey/NotesTab";
import TasksTab from "@/pages/journey/TasksTab";
import MailTab from "@/pages/journey/MailTab";
import AppointmentsTab from "@/pages/journey/AppointmentsTab";

const PIPELINE_STAGES = [
  { value: "new", label: "Neu" },
  { value: "contact_running", label: "Kontakt läuft" },
  { value: "appointment_pending", label: "Termin ausstehend" },
  { value: "appointment_booked", label: "Termin gebucht" },
  { value: "first_call_done", label: "Erstgespräch geführt" },
  { value: "qualification_running", label: "Qualifizierung läuft" },
  { value: "information_requested", label: "Unterlagen angefordert" },
  { value: "site_check_running", label: "Standortprüfung läuft" },
  { value: "concept_phase", label: "Konzeptphase" },
  { value: "proposal_next_step", label: "Angebot / nächster Schritt" },
  { value: "waiting_for_lead", label: "Warten auf Lead" },
  { value: "opportunity", label: "Projektchance" },
  { value: "won_project", label: "Gewonnen" },
  { value: "not_a_fit", label: "Nicht passend" },
  { value: "lost", label: "Verloren" },
  { value: "archived", label: "Archiviert" },
];

const TABS = [
  { id: "overview", label: "Übersicht", icon: LayoutGrid },
  { id: "crm", label: "CRM", icon: Settings2 },
  { id: "tasks", label: "Aufgaben", icon: ListTodo },
  { id: "notes", label: "Notizen", icon: StickyNote },
  { id: "appointments", label: "Termine", icon: CalendarDays },
  { id: "mail", label: "Mailstatus", icon: Mail },
  { id: "history", label: "Verlauf", icon: History },
];

const PRIORITY_TONE = {
  low: "border-zinc-600/40 bg-zinc-700/20 text-zinc-300",
  normal: "border-white/10 bg-white/[0.04] text-zinc-300",
  high: "border-amber-500/40 bg-amber-500/10 text-amber-300",
  urgent: "border-red-500/40 bg-red-500/10 text-red-300",
};

function priorityLabel(value) {
  return PRIORITY_OPTIONS.find((o) => o.value === value)?.label || value;
}

function formatDate(iso, opts = {}) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      ...(opts.withTime !== false ? { hour: "2-digit", minute: "2-digit" } : {}),
    });
  } catch { return iso; }
}

function Row({ label, value, testId }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div data-testid={testId} className="grid grid-cols-1 gap-1 border-b border-white/[0.04] py-3 sm:grid-cols-[200px_1fr]">
      <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className="text-sm text-zinc-200">{value}</div>
    </div>
  );
}

const inputClass =
  "w-full rounded border border-white/10 bg-[#0B0F10] px-3 py-2 text-sm text-zinc-100 outline-none transition focus:border-emerald-500/40";

function authHeaders() {
  const t = readToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function LeadDetail() {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [openTaskForm, setOpenTaskForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/admin/crm/leads/${id}`, {
        headers: authHeaders(), timeout: 20000,
      });
      setLead(r.data);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      setError(status === 404 ? "Lead nicht gefunden." : detail || err?.message || "Unbekannter Fehler");
      setLead(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadTimeline = useCallback(async () => {
    // legacy timeline preload kept for compat; HistoryTab now loads itself.
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (activeTab === "history") loadTimeline(); }, [activeTab, loadTimeline]);

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-10 animate-pulse rounded bg-white/[0.03]" />
        ))}
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div data-testid="journey-lead-error" className="mx-auto max-w-3xl rounded-md border border-red-500/30 bg-red-500/[0.05] p-5 text-sm text-red-200">
        <div className="mb-2 flex items-center gap-2 font-medium">
          <AlertCircle className="h-4 w-4" />
          {error || "Unbekannter Fehler"}
        </div>
        <button
          type="button" onClick={load}
          className="inline-flex items-center gap-1.5 rounded border border-red-400/30 px-3 py-1.5 text-xs text-red-100 transition hover:bg-red-500/10"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Erneut versuchen
        </button>
      </div>
    );
  }

  const stageLabel = PIPELINE_STAGES.find((s) => s.value === lead.pipeline_stage)?.label || lead.pipeline_stage_label;

  return (
    <div data-testid="journey-lead-detail" className="mx-auto max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <Link
          to="/admin/journey/inbox"
          data-testid="journey-lead-back"
          className="inline-flex items-center gap-1.5 text-xs text-zinc-400 transition hover:text-zinc-100"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Zurück zur Inbox
        </Link>
        <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-400/70">
          {lead.created_via === "admin_manual_entry" ? "Manuell erfasst" : "Website-Lead"}
        </span>
      </div>

      <header className="space-y-2 border-b border-white/[0.06] pb-5">
        <div className="flex flex-wrap items-center gap-2 text-[11px]">
          <span className="inline-flex items-center rounded border border-white/10 bg-white/[0.04] px-2 py-0.5 text-zinc-300">
            {lead.kind_label}
          </span>
          <span data-testid="journey-lead-stage-badge" className="inline-flex items-center rounded border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-emerald-300">
            {stageLabel}
          </span>
          <span className="inline-flex items-center rounded border border-white/5 px-2 py-0.5 text-zinc-500">
            {[lead.source_type_label, lead.source_label].filter(Boolean).join(" · ")}
          </span>
          {lead.internal?.priority && lead.internal.priority !== "normal" ? (
            <span
              data-testid="journey-lead-priority-badge"
              className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 ${PRIORITY_TONE[lead.internal.priority] || PRIORITY_TONE.normal}`}
            >
              <Flag className="h-3 w-3" />
              {priorityLabel(lead.internal.priority)}
            </span>
          ) : null}
        </div>
        <h1 className="text-xl font-semibold text-zinc-100">
          {lead.full_name || lead.email || "—"}
        </h1>
        {lead.company ? (
          <p className="text-sm text-zinc-400">
            {lead.company}
            {lead.role ? ` · ${lead.role}` : ""}
          </p>
        ) : null}
      </header>

      <NextStepBox lead={lead} onCreateTask={() => { setActiveTab("tasks"); setOpenTaskForm(true); }} />

      <nav
        data-testid="journey-lead-tabs"
        className="flex flex-wrap gap-1 border-b border-white/[0.06] pb-2"
      >
        {TABS.map(({ id: tid, label, icon: Icon }) => (
          <button
            key={tid}
            type="button"
            data-testid={`journey-lead-tab-${tid}`}
            onClick={() => setActiveTab(tid)}
            className={[
              "inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-xs transition",
              activeTab === tid
                ? "bg-emerald-500/10 text-emerald-300"
                : "text-zinc-400 hover:bg-white/[0.04] hover:text-zinc-100",
            ].join(" ")}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </nav>

      {activeTab === "overview" ? <OverviewTab lead={lead} /> : null}
      {activeTab === "crm" ? (
        <CrmTab lead={lead} onSaved={async () => { await load(); }} />
      ) : null}
      {activeTab === "mail" ? <MailTab leadId={lead.id} lead={lead} /> : null}
      {activeTab === "history" ? <HistoryTab leadId={lead.id} /> : null}
      {activeTab === "notes" ? <NotesTab leadId={lead.id} onChange={load} /> : null}
      {activeTab === "tasks" ? <TasksTab leadId={lead.id} onChange={load} autoOpenForm={openTaskForm} /> : null}
      {activeTab === "appointments" ? <AppointmentsTab leadId={lead.id} onChange={load} /> : null}
    </div>
  );
}

function NextStepBox({ lead, onCreateTask }) {
  const step = lead.internal?.next_step;
  const at = lead.internal?.next_follow_up_at;
  const hasOpenTask = lead.has_open_tasks;
  if (!step && !at && !hasOpenTask) {
    return (
      <div
        data-testid="journey-lead-no-next-step"
        className="flex flex-col items-start justify-between gap-3 rounded-md border border-amber-500/20 bg-amber-500/[0.04] p-4 text-xs text-amber-200/90 sm:flex-row sm:items-center"
      >
        <div>
          <Flag className="mr-1.5 inline h-3.5 w-3.5" />
          Dieser Lead hat noch keinen nächsten Schritt. Aufgabe oder Follow-up anlegen.
        </div>
        <button
          type="button"
          onClick={onCreateTask}
          data-testid="journey-lead-create-task-cta"
          className="inline-flex items-center gap-1.5 rounded bg-emerald-500 px-3 py-1.5 text-[11px] font-medium text-zinc-950 hover:bg-emerald-400"
        >
          Aufgabe anlegen
        </button>
      </div>
    );
  }
  return (
    <div
      data-testid="journey-lead-next-step"
      className="space-y-1 rounded-md border border-emerald-500/20 bg-emerald-500/[0.04] p-4 text-sm text-emerald-100"
    >
      <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-300/70">
        Nächster Schritt
      </div>
      {step ? <div className="font-medium">{step}</div> : null}
      {at ? <div className="text-xs text-emerald-200/80">Geplant für {formatDate(at)}</div> : null}
      {!step && hasOpenTask ? (
        <div className="text-xs text-emerald-200/80">
          {lead.open_tasks_count} offene Aufgabe{lead.open_tasks_count === 1 ? "" : "n"}.
        </div>
      ) : null}
    </div>
  );
}

function OverviewTab({ lead }) {
  return (
    <section className="rounded-md border border-white/[0.06] bg-white/[0.01] px-5">
      <Row label="E-Mail" value={lead.email} />
      <Row label="Telefon" value={lead.phone} />
      <Row label="Website" value={lead.website} />
      <Row label="Rolle" value={lead.role} />
      <Row label="Region" value={lead.city_region || lead.wish_region} />
      <Row label="System / Interesse" value={lead.product_interest || lead.system_interests} />
      <Row label="Zielgruppe" value={lead.target_group} />
      <Row label="Zeitplan" value={lead.timeline} />
      <Row label="Standort vorhanden?" value={lead.has_location} />
      <Row label="Standorttyp" value={lead.location_type} />
      <Row label="Fläche" value={lead.available_area} />
      <Row label="Raumhöhe" value={lead.ceiling_height} />
      <Row label="Aktueller Zustand" value={lead.current_status} />
      <Row label="Gründer-Rolle" value={lead.founder_role} />
      <Row label="Status (Legacy)" value={lead.lead_status_label} />
      <Row label="Quelle" value={[lead.source_type_label, lead.source_label].filter(Boolean).join(" · ")} />
      <Row label="Eintragsseite" value={lead.source_page} />
      <Row label="Erfasst über" value={lead.created_via} />
      <Row label="Tags" value={(lead.tags || []).length > 0 ? lead.tags.join(", ") : null} />
      <Row label="Erstellt am" value={formatDate(lead.created_at)} />
      <Row label="Zuletzt aktualisiert" value={formatDate(lead.updated_at)} />
      {lead.message ? (
        <div className="space-y-1 border-b border-white/[0.04] py-3">
          <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Nachricht</div>
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-200">{lead.message}</p>
        </div>
      ) : null}
    </section>
  );
}

function CrmTab({ lead, onSaved }) {
  const [savingStage, setSavingStage] = useState(false);
  const [savingFields, setSavingFields] = useState(false);

  const [stage, setStage] = useState(lead.pipeline_stage || "new");
  const [stageNote, setStageNote] = useState("");

  const [priority, setPriority] = useState(lead.internal?.priority || "normal");
  const [nextStep, setNextStep] = useState(lead.internal?.next_step || "");
  const [followUpAt, setFollowUpAt] = useState(
    lead.internal?.next_follow_up_at
      ? new Date(lead.internal.next_follow_up_at).toISOString().slice(0, 16)
      : ""
  );
  const [owner, setOwner] = useState(lead.internal?.owner || "admin");
  const [tagsInput, setTagsInput] = useState((lead.tags || []).join(", "));

  const tagsArray = useMemo(
    () => tagsInput.split(",").map((t) => t.trim()).filter(Boolean),
    [tagsInput]
  );

  const saveStage = async () => {
    setSavingStage(true);
    try {
      await axios.patch(
        `${API}/admin/crm/leads/${lead.id}/stage`,
        { pipeline_stage: stage, note: stageNote || undefined },
        { headers: authHeaders(), timeout: 15000 }
      );
      toast.success("Pipeline-Stage aktualisiert");
      setStageNote("");
      await onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.message || "Speichern fehlgeschlagen");
    } finally {
      setSavingStage(false);
    }
  };

  const saveFields = async () => {
    setSavingFields(true);
    try {
      const payload = {
        tags: tagsArray,
        internal: {
          priority,
          next_step: nextStep || null,
          next_follow_up_at: followUpAt ? new Date(followUpAt).toISOString() : null,
          owner: owner || null,
        },
      };
      await axios.patch(
        `${API}/admin/crm/leads/${lead.id}`,
        payload,
        { headers: authHeaders(), timeout: 15000 }
      );
      toast.success("CRM-Felder gespeichert");
      await onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(" · ") : detail || err?.message;
      toast.error(msg || "Speichern fehlgeschlagen");
    } finally {
      setSavingFields(false);
    }
  };

  return (
    <div className="space-y-5">
      <section
        data-testid="journey-crm-stage-section"
        className="space-y-4 rounded-md border border-white/[0.06] bg-white/[0.01] p-5"
      >
        <header>
          <h2 className="text-sm font-semibold text-zinc-100">Pipeline-Stage</h2>
          <p className="text-xs text-zinc-500">
            Operative Stage im CRM. Der Legacy-Status („{lead.lead_status_label}") bleibt unverändert.
          </p>
        </header>
        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
          <label className="space-y-1.5">
            <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-400">Stage</span>
            <div className="relative">
              <select
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                data-testid="journey-crm-stage-select"
                className={`${inputClass} appearance-none pr-8`}
              >
                {PIPELINE_STAGES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
            </div>
          </label>
          <label className="space-y-1.5">
            <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-400">Notiz (optional)</span>
            <input
              type="text"
              value={stageNote}
              onChange={(e) => setStageNote(e.target.value)}
              data-testid="journey-crm-stage-note"
              placeholder="z.B. Erstgespräch sehr stark"
              className={inputClass}
            />
          </label>
          <button
            type="button"
            disabled={savingStage}
            onClick={saveStage}
            data-testid="journey-crm-stage-save"
            className="self-end inline-flex items-center justify-center gap-1.5 rounded bg-emerald-500 px-4 py-2 text-xs font-medium text-zinc-950 transition hover:bg-emerald-400 disabled:opacity-50"
          >
            {savingStage ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Stage speichern
          </button>
        </div>
      </section>

      <section
        data-testid="journey-crm-fields-section"
        className="space-y-4 rounded-md border border-white/[0.06] bg-white/[0.01] p-5"
      >
        <header>
          <h2 className="text-sm font-semibold text-zinc-100">CRM-Felder</h2>
          <p className="text-xs text-zinc-500">
            Priorität, Tags, Owner und nächster operativer Schritt.
          </p>
        </header>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="space-y-1.5">
            <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-400">Priorität</span>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              data-testid="journey-crm-priority"
              className={inputClass}
            >
              {PRIORITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="space-y-1.5">
            <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-400">Owner</span>
            <input
              type="text"
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              data-testid="journey-crm-owner"
              placeholder="admin"
              className={inputClass}
            />
          </label>
        </div>
        <label className="block space-y-1.5">
          <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-400">Nächster Schritt</span>
          <input
            type="text"
            value={nextStep}
            onChange={(e) => setNextStep(e.target.value)}
            data-testid="journey-crm-next-step"
            placeholder="z.B. Standortdaten anfragen"
            className={inputClass}
          />
        </label>
        <label className="block space-y-1.5">
          <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-400">Follow-up Datum / Uhrzeit</span>
          <input
            type="datetime-local"
            value={followUpAt}
            onChange={(e) => setFollowUpAt(e.target.value)}
            data-testid="journey-crm-follow-up-at"
            className={inputClass}
          />
        </label>
        <label className="block space-y-1.5">
          <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-400">
            Tags (Komma-getrennt)
          </span>
          <input
            type="text"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            data-testid="journey-crm-tags"
            placeholder="hotel, warm-lead, pvra"
            className={inputClass}
          />
          {tagsArray.length > 0 ? (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {tagsArray.map((t) => (
                <span key={t} className="inline-flex items-center rounded border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[10px] text-zinc-300">
                  {t}
                </span>
              ))}
            </div>
          ) : null}
        </label>

        <div className="flex justify-end pt-1">
          <button
            type="button"
            disabled={savingFields}
            onClick={saveFields}
            data-testid="journey-crm-fields-save"
            className="inline-flex items-center justify-center gap-1.5 rounded bg-emerald-500 px-4 py-2 text-xs font-medium text-zinc-950 transition hover:bg-emerald-400 disabled:opacity-50"
          >
            {savingFields ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            CRM-Felder speichern
          </button>
        </div>
      </section>
    </div>
  );
}

const EVENT_LABELS = {
  manual_entry: "Lead manuell erfasst",
  lead_created: "Lead erstellt",
  lead_updated: "Lead aktualisiert",
  pipeline_stage_changed: "Stage geändert",
  note_added: "Notiz hinzugefügt",
  note_updated: "Notiz bearbeitet",
  note_edited: "Notiz bearbeitet",
  note_deleted: "Notiz gelöscht",
  task_created: "Aufgabe erstellt",
  task_updated: "Aufgabe aktualisiert",
  task_completed: "Aufgabe erledigt",
  task_skipped: "Aufgabe übersprungen",
  task_deleted: "Aufgabe gelöscht",
  mail_sent: "Mail gesendet",
  mail_failed: "Mail fehlgeschlagen",
  mail_prepared: "Mail vorbereitet",
  assignee_changed: "Verantwortlicher geändert",
  file_uploaded: "Datei hinzugefügt",
};

const EVENT_FILTER_OPTIONS = [
  { value: "", label: "Alle Ereignisse" },
  { value: "manual_entry", label: "Lead manuell erfasst" },
  { value: "lead_updated", label: "Lead aktualisiert" },
  { value: "pipeline_stage_changed", label: "Stage geändert" },
  { value: "note_added", label: "Notiz hinzugefügt" },
  { value: "note_updated", label: "Notiz bearbeitet" },
  { value: "note_deleted", label: "Notiz gelöscht" },
  { value: "task_created", label: "Aufgabe erstellt" },
  { value: "task_updated", label: "Aufgabe aktualisiert" },
  { value: "task_completed", label: "Aufgabe erledigt" },
  { value: "task_deleted", label: "Aufgabe gelöscht" },
  { value: "mail_sent", label: "Mail gesendet" },
  { value: "mail_failed", label: "Mail fehlgeschlagen" },
  { value: "mail_prepared", label: "Mail vorbereitet" },
  { value: "appointment_created", label: "Termin erstellt" },
  { value: "appointment_updated", label: "Termin aktualisiert" },
  { value: "appointment_deleted", label: "Termin gelöscht" },
  { value: "appointment_status_changed", label: "Terminstatus geändert" },
  { value: "appointment_completed", label: "Termin durchgeführt" },
  { value: "appointment_cancelled", label: "Termin abgesagt" },
  { value: "appointment_no_show", label: "No-Show markiert" },
];


function HistoryTab({ leadId }) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [eventType, setEventType] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [appendMode, setAppendMode] = useState(false);

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("page", String(opts.page || page));
      params.set("page_size", String(pageSize));
      if (eventType) params.set("event_type", eventType);
      const r = await axios.get(
        `${API}/admin/crm/leads/${leadId}/timeline?${params}`,
        { headers: authHeaders(), timeout: 20000 },
      );
      const body = r.data;
      setTotal(body.total || 0);
      setTotalPages(body.total_pages || 0);
      setItems((prev) => (opts.append ? [...prev, ...(body.items || [])] : (body.items || [])));
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Fehler");
    } finally {
      setLoading(false);
      setAppendMode(false);
    }
  }, [leadId, page, pageSize, eventType]);

  useEffect(() => { setPage(1); }, [eventType]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load({ page, append: appendMode }); }, [leadId, page, eventType]);

  const loadMore = () => { setAppendMode(true); setPage((p) => p + 1); };

  return (
    <section data-testid="journey-history-tab" className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Filter</span>
          <select
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            data-testid="journey-history-filter-event-type"
            className="rounded border border-white/10 bg-[#0B0F10] px-2 py-1.5 text-xs text-zinc-200 outline-none focus:border-emerald-500/40"
          >
            {EVENT_FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="text-[11px] text-zinc-500">
          {total} Ereignis{total === 1 ? "" : "se"}
        </div>
      </div>

      {error ? (
        <div className="rounded-md border border-red-500/30 bg-red-500/[0.05] p-4 text-xs text-red-200">
          {String(error)}
        </div>
      ) : loading && items.length === 0 ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-12 animate-pulse rounded bg-white/[0.03]" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-white/[0.06] bg-white/[0.01] p-5 text-sm text-zinc-400">
          Noch keine Verlaufseinträge.
        </div>
      ) : (
        <ol data-testid="journey-history-list" className="space-y-2">
          {items.map((e) => (
            <HistoryEvent key={e.id} ev={e} />
          ))}
        </ol>
      )}

      {totalPages > 1 && page < totalPages ? (
        <div className="flex justify-center pt-1">
          <button
            type="button"
            onClick={loadMore}
            disabled={loading}
            data-testid="journey-history-load-more"
            className="rounded border border-white/10 px-3 py-1.5 text-xs text-zinc-300 transition hover:border-white/20 hover:bg-white/5 disabled:opacity-50"
          >
            {loading ? "Lädt…" : `Mehr laden (${total - items.length} verbleibend)`}
          </button>
        </div>
      ) : null}
    </section>
  );
}

function HistoryEvent({ ev }) {
  const label = ev.event_type_label || EVENT_LABELS[ev.event_type] || ev.event_type;
  return (
    <li
      data-testid={`journey-history-event-${ev.event_type}`}
      className="rounded-md border border-white/[0.06] bg-white/[0.01] p-3 text-sm"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-zinc-200">{label}</span>
        <span className="text-[10px] text-zinc-500">{formatDate(ev.created_at)}</span>
      </div>
      <EventPayload type={ev.event_type} payload={ev.payload} />
    </li>
  );
}

function EventPayload({ type, payload }) {
  if (!payload) return null;
  if (type === "pipeline_stage_changed") {
    return (
      <div className="mt-1 text-xs text-zinc-400">
        {payload.old_stage || "—"}
        <span className="mx-1 text-zinc-600">→</span>
        <span className="text-emerald-300">{payload.new_stage}</span>
        {payload.note ? <div className="mt-0.5 text-[11px] text-zinc-500">Notiz: {payload.note}</div> : null}
      </div>
    );
  }
  if (type === "lead_updated" && Array.isArray(payload.changed_fields)) {
    return (
      <div className="mt-1 flex flex-wrap gap-1 text-[11px] text-zinc-500">
        {payload.changed_fields.map((f) => (
          <span key={f} className="rounded border border-white/10 bg-white/[0.04] px-1.5 py-0.5">{f}</span>
        ))}
      </div>
    );
  }
  if (type === "task_created" || type === "task_updated" || type === "task_completed" || type === "task_deleted") {
    return (
      <div className="mt-1 text-xs text-zinc-400">
        {payload.title ? <span>{payload.title}</span> : null}
        {payload.status ? <span className="ml-2 text-zinc-500">Status: {payload.status}</span> : null}
        {payload.due_at ? <span className="ml-2 text-zinc-500">Fällig: {formatDate(payload.due_at)}</span> : null}
      </div>
    );
  }
  if (type === "note_added" || type === "note_updated" || type === "note_edited" || type === "note_deleted") {
    if (payload.note_type) {
      return <div className="mt-1 text-xs text-zinc-500">Typ: {payload.note_type}</div>;
    }
    return null;
  }
  if (type === "manual_entry") {
    return payload.source_type ? (
      <div className="mt-1 text-xs text-zinc-500">
        Quelle: {payload.source_type}
        {payload.kind ? ` · ${payload.kind}` : ""}
      </div>
    ) : null;
  }
  // Fallback: show compact JSON details.
  return (
    <details className="mt-1">
      <summary className="cursor-pointer text-[10px] text-zinc-500 hover:text-zinc-300">Details</summary>
      <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap rounded bg-black/30 p-2 text-[10px] text-zinc-400">
        {JSON.stringify(payload, null, 2)}
      </pre>
    </details>
  );
}
